// BL-241 Gate 3 — pure helpers behind the manual flag/status icon on each Communications thread
// row. Same shape as src/lib/communicationsThread.js: plain functions, no React and no Supabase,
// so the state → icon mapping and the column patches unit test on their own.
//
// Values map onto intake_sessions flag_* columns. BL-333 keeps the existing flag column names for
// compatibility, but expands flag_status and separates "currently active" into flag_active.

export const FLAG_NONE = 'none';
export const FLAG_FOLLOW_UP = 'follow_up';
export const FLAG_URGENT = 'urgent';
export const FLAG_ASAP = 'asap';
export const FLAG_NEEDS_ATTENTION = 'needs_attention';
// BL-177 Gate 17b: someone has taken action and resolution is underway. Not terminal.
export const FLAG_IN_PROGRESS = 'in_progress';
export const FLAG_RESOLVED = 'resolved';
export const FLAG_CLOSED = 'closed';

export const FLAG_STATUSES = [
    FLAG_NONE,
    FLAG_FOLLOW_UP,
    FLAG_URGENT,
    FLAG_ASAP,
    FLAG_NEEDS_ATTENTION,
    FLAG_IN_PROGRESS,
    FLAG_RESOLVED,
    FLAG_CLOSED,
];

export const FLAG_SOURCE_MANUAL = 'manual';

export const FLAG_COLOR_CLASSES = {
    none: 'text-gray-400',
    followUp: 'text-[var(--comms-flag-followup)]',
    followThru: 'text-[var(--comms-flag-followthru)]',
    urgent: 'text-[var(--comms-flag-urgent)]',
    asap: 'text-[var(--comms-flag-asap)]',
    needsAttention: 'text-[var(--comms-flag-needs-attention)]',
    inProgress: 'text-[var(--comms-flag-in-progress)]',
    resolved: 'text-[var(--comms-flag-resolved)]',
    closed: 'text-[var(--comms-flag-closed)]',
};

export const FLAG_LABELS = {
    [FLAG_NONE]: 'Clear flag',
    [FLAG_FOLLOW_UP]: 'Follow up',
    [FLAG_URGENT]: 'Urgent',
    [FLAG_ASAP]: 'ASAP',
    [FLAG_NEEDS_ATTENTION]: 'Needs attention',
    [FLAG_IN_PROGRESS]: 'In progress',
    [FLAG_RESOLVED]: 'Resolved',
    [FLAG_CLOSED]: 'Closed',
};

/** Urgency only applies to follow_thru, and only as 1 / 2 / 3. Anything else clamps or drops. */
export function normalizeFlagUrgency(value) {
    const parsed = typeof value === 'number' ? value : parseInt(value, 10);
    if (!Number.isFinite(parsed)) return null;
    if (parsed <= 1) return 1;
    if (parsed >= 3) return 3;
    return 2;
}

export function normalizeFlagStatus(value) {
    const cleaned = typeof value === 'string' ? value.trim().toLowerCase() : '';
    return FLAG_STATUSES.includes(cleaned) ? cleaned : FLAG_NONE;
}

export function normalizeSessionFlagStatus(session = {}) {
    const cleaned = typeof session?.flag_status === 'string' ? session.flag_status.trim().toLowerCase() : '';
    if (cleaned === 'follow_thru') {
        const urgency = normalizeFlagUrgency(session?.flag_urgency) || 1;
        if (urgency >= 3) return FLAG_URGENT;
        if (urgency === 2) return FLAG_ASAP;
        return FLAG_NEEDS_ATTENTION;
    }
    return normalizeFlagStatus(cleaned);
}

/** "!" / "!!" / "!!!" for urgency 1 / 2 / 3. */
export function urgencyMarks(urgency) {
    return '!'.repeat(normalizeFlagUrgency(urgency) || 1);
}

/** Half-filled circle: work has started but isn't finished. */
export const IN_PROGRESS_GLYPH = '◐';

/**
 * The icon a thread row shows: orange flag for follow_up, red !/!!/!!! for follow_thru,
 * green check for resolved, and null for 'none' (no icon at all).
 */
export function describeFlag(session) {
    const status = normalizeSessionFlagStatus(session);
    if (status === FLAG_NONE) {
        return {
            status,
            urgency: null,
            glyph: '⚑',
            label: FLAG_LABELS[status],
            className: FLAG_COLOR_CLASSES.none,
            set: false,
        };
    }
    if (status === FLAG_FOLLOW_UP) {
        return {
            status,
            urgency: null,
            glyph: '⚑',
            label: FLAG_LABELS[status],
            className: FLAG_COLOR_CLASSES.followUp,
            set: true,
        };
    }
    if (status === FLAG_URGENT || status === FLAG_ASAP || status === FLAG_NEEDS_ATTENTION) {
        const glyph = status === FLAG_URGENT ? '!!!' : (status === FLAG_ASAP ? '!!' : '!');
        const colorClass = status === FLAG_URGENT
            ? FLAG_COLOR_CLASSES.urgent
            : (status === FLAG_ASAP ? FLAG_COLOR_CLASSES.asap : FLAG_COLOR_CLASSES.needsAttention);
        return {
            status,
            urgency: null,
            glyph,
            label: FLAG_LABELS[status],
            className: `${colorClass} font-bold`,
            set: true,
        };
    }
    if (status === FLAG_IN_PROGRESS) {
        return {
            status,
            urgency: null,
            glyph: IN_PROGRESS_GLYPH,
            label: FLAG_LABELS[status],
            className: FLAG_COLOR_CLASSES.inProgress,
            set: true,
        };
    }
    if (status === FLAG_RESOLVED) {
        return {
            status,
            urgency: null,
            glyph: '✓',
            label: FLAG_LABELS[status],
            className: FLAG_COLOR_CLASSES.resolved,
            set: true,
        };
    }
    if (status === FLAG_CLOSED) {
        return {
            status,
            urgency: null,
            glyph: '⊘',
            label: FLAG_LABELS[status],
            className: FLAG_COLOR_CLASSES.closed,
            set: true,
        };
    }
    return describeFlag({ flag_status: FLAG_NONE });
}

/** The menu the hover icon opens: all statuses plus the clear/default state. */
export const FLAG_MENU_OPTIONS = [
    // Gate 17e: "Clear flag" gets its own glyph so it can't be mistaken for "Follow up" (⚑).
    { key: 'none', status: FLAG_NONE, urgency: null, label: FLAG_LABELS[FLAG_NONE], glyph: '✕', className: FLAG_COLOR_CLASSES.none },
    { key: 'follow_up', status: FLAG_FOLLOW_UP, urgency: null, label: FLAG_LABELS[FLAG_FOLLOW_UP], glyph: '⚑', className: FLAG_COLOR_CLASSES.followUp },
    { key: 'urgent', status: FLAG_URGENT, urgency: null, label: FLAG_LABELS[FLAG_URGENT], glyph: '!!!', className: `${FLAG_COLOR_CLASSES.urgent} font-bold` },
    { key: 'asap', status: FLAG_ASAP, urgency: null, label: FLAG_LABELS[FLAG_ASAP], glyph: '!!', className: `${FLAG_COLOR_CLASSES.asap} font-bold` },
    { key: 'needs_attention', status: FLAG_NEEDS_ATTENTION, urgency: null, label: FLAG_LABELS[FLAG_NEEDS_ATTENTION], glyph: '!', className: `${FLAG_COLOR_CLASSES.needsAttention} font-bold` },
    { key: 'in_progress', status: FLAG_IN_PROGRESS, urgency: null, label: FLAG_LABELS[FLAG_IN_PROGRESS], glyph: IN_PROGRESS_GLYPH, className: FLAG_COLOR_CLASSES.inProgress },
    { key: 'resolved', status: FLAG_RESOLVED, urgency: null, label: FLAG_LABELS[FLAG_RESOLVED], glyph: '✓', className: FLAG_COLOR_CLASSES.resolved },
    { key: 'closed', status: FLAG_CLOSED, urgency: null, label: FLAG_LABELS[FLAG_CLOSED], glyph: '⊘', className: FLAG_COLOR_CLASSES.closed },
];

/**
 * The intake_sessions patch for a manual flag change.
 *
 * Setting any flag stamps flagged_at / flagged_by / flag_source='manual'; resolving additionally
 * stamps resolved_at / resolved_by; clearing resets flag_status to 'none' and nulls the rest.
 * Non-resolved states null resolved_at / resolved_by so a re-flagged thread doesn't keep a stale
 * resolution on it.
 */
export function isTerminalFlagStatus(status) {
    const normalized = normalizeFlagStatus(status);
    return normalized === FLAG_RESOLVED || normalized === FLAG_CLOSED;
}

export function shouldPulseStatusIcon(session = {}) {
    if (isConversationClosed(session)) return false;
    const status = normalizeSessionFlagStatus(session);
    return Boolean(
        session?.flag_active
        && [FLAG_FOLLOW_UP, FLAG_URGENT, FLAG_ASAP, FLAG_NEEDS_ATTENTION, FLAG_IN_PROGRESS].includes(status)
    );
}

export function buildFlagPatch({ status, active = false, userId = null, now = new Date() } = {}) {
    const nextStatus = normalizeFlagStatus(status);
    const stamp = (now instanceof Date ? now : new Date(now)).toISOString();
    const nextActive = isTerminalFlagStatus(nextStatus) ? false : Boolean(active);

    if (nextStatus === FLAG_NONE) {
        return {
            flag_status: FLAG_NONE,
            flag_urgency: null,
            flag_active: nextActive,
            flagged_at: null,
            flagged_by: null,
            flag_source: null,
            resolved_at: null,
            resolved_by: null,
        };
    }

    return {
        flag_status: nextStatus,
        flag_urgency: null,
        flag_active: nextActive,
        flagged_at: stamp,
        flagged_by: userId || null,
        flag_source: FLAG_SOURCE_MANUAL,
        resolved_at: nextStatus === FLAG_RESOLVED ? stamp : null,
        resolved_by: nextStatus === FLAG_RESOLVED ? (userId || null) : null,
    };
}

/** True when the given menu option matches the session's current flag state. */
export function isActiveFlagOption(session, option) {
    // A closed conversation's menu marks Closed, matching the circle (Gate 17c).
    const status = isConversationClosed(session) ? FLAG_CLOSED : normalizeSessionFlagStatus(session);
    return option?.status === status;
}

// ---------------------------------------------------------------------------
// BL-177 Gate 17c — closed conversations go quiet
// ---------------------------------------------------------------------------

/** The one grey every alert on a closed conversation falls back to (same as the Closed tag). */
export const QUIET_GREY = '#6B7280';
export const QUIET_TEXT_CLASS = 'text-[#6B7280]';
export const QUIET_TAG_CLASS = 'border-[#6B7280] text-[#6B7280]';

/**
 * True when a conversation reads as Closed: staff set the status circle to Closed, or the thread
 * itself was closed (idle close sets approval_status 'closed'; an abandoned outcome is also shown
 * as Closed). Archived conversations are never "closed-quiet" — they keep their own look.
 * Reopening clears it: a manual status change (see buildManualReopenPatch) or the Gate 17 inbound
 * rule (see buildInboundClosedFlagReopenPatch in twilioIntake.js).
 */
export function isConversationClosed(session) {
    if (!session || session.archived_at) return false;
    if (normalizeSessionFlagStatus(session) === FLAG_CLOSED) return true;
    const approval = typeof session.approval_status === 'string' ? session.approval_status.trim().toLowerCase() : '';
    const outcome = typeof session.outcome === 'string' ? session.outcome.trim().toLowerCase() : '';
    return approval === 'closed' || outcome === 'abandoned';
}

/** What the status circle shows: the Closed state only (in grey) while closed, else the flag. */
export function describeStatusCircle(session) {
    if (!isConversationClosed(session)) return describeFlag(session);
    return {
        status: FLAG_CLOSED,
        urgency: null,
        glyph: '⊘',
        label: FLAG_LABELS[FLAG_CLOSED],
        className: QUIET_TEXT_CLASS,
        set: true,
    };
}

/**
 * Extra intake_sessions columns for a manual status change that reopens a thread the system
 * closed. Choosing any status other than Closed on a closed thread puts it back where the Gate 17
 * inbound reopen puts it (approval_status 'pending', outcome 'in_progress'). A thread that is only
 * flag-Closed needs nothing extra: the flag patch itself reopens it.
 */
export function buildManualReopenPatch(session, nextStatus) {
    if (!session || normalizeFlagStatus(nextStatus) === FLAG_CLOSED) return {};
    const approval = typeof session.approval_status === 'string' ? session.approval_status.trim().toLowerCase() : '';
    const outcome = typeof session.outcome === 'string' ? session.outcome.trim().toLowerCase() : '';
    const patch = {};
    if (approval === 'closed') patch.approval_status = 'pending';
    if (approval === 'closed' || outcome === 'abandoned') patch.outcome = 'in_progress';
    return patch;
}
